import { EvaluatedValue, FileKind, Input, NodeId } from 'web';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { ExportData } from '@/types/node';
import { HandleType } from '@/enums/handle-type.enum';
import { getFileExtension, parseFilenameFromContentDisposition } from '@/utils/urls';
import { extensionFromMimeType } from '@/utils/mimeTypes';
import { assetUrl, isBlobstoreResolved, type DisplayFile } from '@/utils/assetUrls';
import { useWorkflowStore } from '@/state/workflow.state';
import { markRenderedPicture } from '../NodeAssetDownloader/markRenderedPicture';
import type { ResolvedAssets } from '@/types/api/assets';
import type { FlowGraph } from '../FlowGraph';

const EXPORTABLE_TYPES = [HandleType.Image, HandleType.Mask, HandleType.Video, HandleType.Audio, HandleType.ThreeDee];

export type VideoExportFormat = 'mp4' | 'mov-prores' | 'mov-h264' | 'webm';

type FileInfo = {
  url?: string;
  blob?: Blob;
  /** Set instead of `blob` when the caller asked for a stream, so large renders never sit in memory. */
  stream?: ReadableStream<Uint8Array>;
  type: FileKind['type'];
  extension?: string;
};
type FileResult = { fileInfo: FileInfo | null; error: string | null };

/** Shared by the Export node and on-node download button. */
export async function resolveDownloadableVideoAudioFile(
  evaluatedFile: EvaluatedValue,
  inputNode: Input,
  workflowId: string,
  saveRecipe: () => Promise<void>,
  resolved: ResolvedAssets,
  format?: VideoExportFormat,
  signal?: AbortSignal,
  responseAs: 'blob' | 'stream' = 'blob',
): Promise<FileResult> {
  const fileType = evaluatedFile.type as FileKind['type'];
  // Handle computed videos/audio - return blob directly
  if (
    typeof evaluatedFile.value === 'object' &&
    'type' in evaluatedFile.value &&
    evaluatedFile.value.type === 'computed'
  ) {
    const nodeId = inputNode.nodeId;
    const outputId = inputNode.outputId;
    await saveRecipe();
    signal?.throwIfAborted();
    // Must stay a POST even though it only reads. The server renders the video in the request, and a
    // large render can kill its pod; a proxy replays a GET whose pod dropped, so one export would take
    // down pod after pod. A POST is never replayed.
    // A multi-GB render (e.g. ProRes) buffered as a Blob crashes the tab, so callers that can write to
    // disk incrementally take the body as a stream; only the fetch adapter can return one.
    const res = await safeAxios.post<Blob | ReadableStream<Uint8Array>>(
      API_ROUTES.workflows.nodeOutputVideoDownload({ workflowId, nodeId, outputId }),
      undefined,
      {
        ...(responseAs === 'stream' ? { adapter: 'fetch', responseType: 'stream' } : { responseType: 'blob' }),
        signal,
        params: format ? { format } : undefined,
        'axios-retry': { retries: 0 },
      },
    );
    if (!res.data) {
      return { fileInfo: null, error: 'File not found' };
    }
    // Server's Content-Disposition has the authoritative extension (e.g. .mov vs .mp4 vs .webm).
    // Pulling it from there avoids client-side mime-derivation guesswork.
    const serverFilename = parseFilenameFromContentDisposition(res.headers['content-disposition']);
    const extension = serverFilename?.includes('.') ? serverFilename.split('.').pop() : undefined;
    const body = res.data instanceof Blob ? { blob: res.data } : { stream: res.data };
    return {
      fileInfo: { ...body, type: fileType, extension },
      error: null,
    };
  }

  // Handle non-computed videos/audio
  if (typeof evaluatedFile.value === 'object' && 'url' in evaluatedFile.value) {
    return { fileInfo: storedFileInfo(evaluatedFile.value, fileType, resolved), error: null };
  }

  return { fileInfo: null, error: 'Invalid file data' };
}

export class ExportView {
  constructor(
    private graph: FlowGraph,
    private id: NodeId,
  ) {}

  isExportable(): boolean {
    const inputNode = this.graph.store.getNodeDataNullable<ExportData>(this.id)?.inputNode;
    if (!inputNode || !this.graph.store.getNodeNullable(inputNode.nodeId)) {
      return false;
    }

    const outputType = this.graph.nodeOutputType(inputNode.nodeId, inputNode.outputId);
    return outputType !== undefined && EXPORTABLE_TYPES.includes(outputType);
  }

  /** True when the connected upstream output is specifically a Video handle. */
  isVideoConnected(): boolean {
    const inputNode = this.graph.store.getNodeDataNullable<ExportData>(this.id)?.inputNode;
    if (!inputNode || !this.graph.store.getNodeNullable(inputNode.nodeId)) {
      return false;
    }

    return this.graph.nodeOutputType(inputNode.nodeId, inputNode.outputId) === HandleType.Video;
  }

  /**
   * True when the connected video would be re-encoded server-side (computed). URL-backed
   * videos bypass `/download-video` entirely (`resolveDownloadableVideoAudioFile` returns the raw URL),
   * so the format picker has no effect on them — we hide it for those.
   */
  isComputedVideoConnected(): boolean {
    if (!this.isVideoConnected()) {
      return false;
    }

    const inputNode = this.graph.store.getNodeDataNullable<ExportData>(this.id)?.inputNode;
    if (!inputNode) {
      return false;
    }

    const evaluated = this.graph.evaluateOutput(inputNode);
    const value = evaluated?.value;
    return typeof value === 'object' && value !== null && 'type' in value && value.type === 'computed';
  }

  private validateAndGetEvaluatedFile():
    { success: true; evaluatedFile: EvaluatedValue; inputNode: Input } | { success: false; error: string } {
    if (!this.isExportable()) {
      return { success: false, error: 'Node is not exportable' };
    }

    const inputNode = this.graph.store.getNodeDataNullable<ExportData>(this.id)?.inputNode;
    if (!inputNode) {
      return { success: false, error: 'Input not found' };
    }

    const evaluatedFile = this.graph.evaluateOutput(inputNode);
    if (!evaluatedFile) {
      return { success: false, error: 'Evaluated file not found' };
    }

    return { success: true, evaluatedFile, inputNode };
  }

  private async resolveImageFile(
    evaluatedFile: EvaluatedValue,
    inputNode: Input,
    resolved: ResolvedAssets,
    workflowId: string,
  ): Promise<FileResult> {
    // Handle computed images by rendering them, and marking them against the node that rendered them
    if (
      typeof evaluatedFile.value === 'object' &&
      'type' in evaluatedFile.value &&
      evaluatedFile.value.type === 'computed'
    ) {
      const computedImage = this.graph.wasm.call((wasm) => wasm.exportNodeRender(inputNode.nodeId, inputNode.outputId));
      if (!computedImage) {
        return { fileInfo: null, error: 'Computed image not found' };
      }
      const picture = await markRenderedPicture({ workflowId, nodeId: inputNode.nodeId, dataUrl: computedImage.data });
      return { fileInfo: { ...picture, type: 'image' }, error: null };
    }

    // Handle non-computed images
    if (typeof evaluatedFile.value === 'object' && 'url' in evaluatedFile.value) {
      return { fileInfo: storedFileInfo(evaluatedFile.value, 'image', resolved), error: null };
    }

    return { fileInfo: null, error: 'Invalid image data' };
  }

  private resolveSimpleAssetFile(evaluatedFile: EvaluatedValue, resolved: ResolvedAssets): FileResult {
    const fileType = evaluatedFile.type as FileKind['type'];
    if (typeof evaluatedFile.value === 'object' && 'url' in evaluatedFile.value) {
      return { fileInfo: storedFileInfo(evaluatedFile.value, fileType, resolved), error: null };
    }
    return { fileInfo: null, error: 'Invalid asset data' };
  }

  /**
   * Resolves and prepares a file for download. For computed files, this may involve rendering (images)
   * or downloading from the server (videos). Returns the file URL and type, or an error if unsuccessful.
   *
   * `videoFormat` is forwarded to the server for video downloads — set when the user explicitly
   * picks an export format (mp4 / mov / webm). Ignored for non-video file types.
   */
  async resolveDownloadableFile(
    recipeId: string,
    saveRecipe: () => Promise<void>,
    videoFormat?: VideoExportFormat,
    responseAs: 'blob' | 'stream' = 'blob',
  ): Promise<FileResult> {
    const validation = this.validateAndGetEvaluatedFile();
    if (!validation.success) {
      return { fileInfo: null, error: validation.error };
    }

    const { evaluatedFile, inputNode } = validation;
    const resolved = useWorkflowStore.getState().resolvedAssets;
    const fileType = evaluatedFile.type;
    switch (fileType) {
      case 'image':
        return await this.resolveImageFile(evaluatedFile, inputNode, resolved, recipeId);
      case 'video':
      case 'audio':
        return await resolveDownloadableVideoAudioFile(
          evaluatedFile,
          inputNode,
          recipeId,
          saveRecipe,
          resolved,
          videoFormat,
          undefined,
          responseAs,
        );
      case '3D':
        return this.resolveSimpleAssetFile(evaluatedFile, resolved);
      default:
        return { fileInfo: null, error: 'File type not supported' };
    }
  }
}

function storedFileInfo(
  file: DisplayFile & { url: string; mime?: string },
  type: FileKind['type'],
  resolved: ResolvedAssets,
): FileInfo {
  if (isBlobstoreResolved(file, resolved)) {
    return { url: assetUrl(file, resolved), type, extension: extensionFromMimeType(file.mime, type) };
  }

  return { url: file.url, type, extension: getFileExtension(file.url, type) };
}
